/**
 * Device Calendar Import Service
 *
 * SETUP: Install the Capacitor Calendar plugin:
 *   npm install @ebarooni/capacitor-calendar
 *   npx cap sync
 *
 * This wraps the native EventKit (iOS) / CalendarContract (Android) bridge
 * and normalises the output into the shape our backend /calendar-sync/native/import
 * endpoint expects. Gracefully degrades on web.
 */

import { Capacitor } from '@capacitor/core';

export interface DeviceEventInput {
  id: string;
  title: string;
  description?: string;
  start_date: string;       // YYYY-MM-DD
  end_date?: string | null;
  start_time?: string | null; // HH:MM
  end_time?: string | null;
  all_day: boolean;
  location?: string | null;
  calendar_id?: string | null;
}

export const isDeviceCalendarAvailable = (): boolean => Capacitor.isNativePlatform();

export const getPlatform = (): 'ios' | 'android' | null => {
  if (!Capacitor.isNativePlatform()) return null;
  const p = Capacitor.getPlatform();
  if (p === 'ios' || p === 'android') return p;
  return null;
};

function pad(n: number) { return n < 10 ? `0${n}` : String(n); }

function isoDate(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoTime(d: Date) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normaliseEvent(raw: any): DeviceEventInput | null {
  if (!raw) return null;
  const id = raw.id;
  const title = raw.title;
  const startMs = typeof raw.startDate === 'number' ? raw.startDate : Number(raw.startDate);
  if (!id || !title || !startMs || Number.isNaN(startMs)) return null;

  const endMs = typeof raw.endDate === 'number' ? raw.endDate : Number(raw.endDate);
  const start = new Date(startMs);
  const end = endMs && !Number.isNaN(endMs) ? new Date(endMs) : start;
  const allDay = !!raw.isAllDay;

  return {
    id: String(id),
    title: String(title).slice(0, 200),
    description: raw.description || '',
    start_date: isoDate(start),
    end_date: isoDate(end),
    start_time: allDay ? null : isoTime(start),
    end_time: allDay ? null : isoTime(end),
    all_day: allDay,
    location: raw.location || null,
    calendar_id: raw.calendarId || null,
  };
}

/**
 * Read events from the device's native calendar store within the given
 * window (defaults to −30 / +180 days). Returns null if unsupported or the
 * user denied permission.
 */
export const readDeviceCalendarEvents = async (opts?: {
  from?: Date;
  to?: Date;
}): Promise<DeviceEventInput[] | null> => {
  if (!isDeviceCalendarAvailable()) return null;

  try {
    const mod: any = await import('@ebarooni/capacitor-calendar');
    const plugin = mod.CapacitorCalendar || mod.default || mod;
    if (!plugin || typeof plugin.listEventsInRange !== 'function') {
      console.warn('[deviceCalendar] plugin missing listEventsInRange');
      return null;
    }

    // Ask for read-only calendar access. iOS 17+ exposes a dedicated
    // method; older iOS + Android fall back to the generic alias API.
    let granted = true;
    if (typeof plugin.requestReadOnlyCalendarAccess === 'function') {
      try {
        const r = await plugin.requestReadOnlyCalendarAccess();
        if (r && r.result && r.result !== 'granted') granted = false;
      } catch {
        // Older iOS — fall through to requestPermission below.
        const r = await plugin.requestPermission({ alias: 'readCalendar' });
        granted = r?.result === 'granted';
      }
    } else {
      const r = await plugin.requestPermission({ alias: 'readCalendar' });
      granted = r?.result === 'granted';
    }
    if (!granted) return null;

    const now = new Date();
    const from = opts?.from ?? new Date(now.getTime() - 30 * 864e5);
    const to = opts?.to ?? new Date(now.getTime() + 180 * 864e5);

    const res: any = await plugin.listEventsInRange({
      startDate: from.getTime(),
      endDate: to.getTime(),
    });

    const rawEvents: any[] = Array.isArray(res) ? res : (res?.result || []);
    return rawEvents.map(normaliseEvent).filter((e): e is DeviceEventInput => !!e);
  } catch (err) {
    console.error('[deviceCalendar] read failed', err);
    return null;
  }
};
